import { Box, Button } from '@chakra-ui/react';
import LineChart from 'components/charts/LineAreaChart';
import React, { useCallback, useEffect, useState } from 'react';
import FilterCalendar from 'components/calendar/FilterCalendar';
import useApi from 'hooks/useApi';
import { CHART_DATA_API } from 'constants/endpoints';
import { OverlayLoaderComponent } from 'components/loader/Loader';
import { showToast } from 'components/toast/Toast';

const DataCharts = () => {
  const { apiCall } = useApi();
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [activeButton, setActiveButton] = useState('7'); // Default active button

  const fetchData = useCallback(async () => {
    setLoading(true);

    // Validate start and end dates
    if (!startDate || !endDate) {
      showToast({
        message: 'Start and end date must be selected.',
        success: false,
        customStyle: {
          background: '#f5222d',
          color: '#fff',
          padding: '10px',
        },
      });
      setLoading(false);
      return;
    }
    if (startDate.toDateString() === endDate.toDateString()) {
      showToast({
        message: 'Start and end date must not be the same. Select a range of dates.',
        success: false,
        customStyle: {
          background: '#f5222d',
          color: '#fff',
          padding: '10px',
        },
      });
      setLoading(false);
      return;
    }

    try {
      const response = await apiCall.get(`${CHART_DATA_API}`, {
        start_date: startDate,
        end_date: endDate,
      });

      updateChartData(response.data);
    } catch (error) {
      console.error("Error fetching user requests:", error);
      showToast({
        message: 'Error fetching data. Please try again.',
        success: false,
        customStyle: {
          background: '#f5222d',
          color: '#fff',
          padding: '10px',
        },
      });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const handleCustomDateChange = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    setStartDate(date);
    setEndDate(new Date());
    setActiveButton(days.toString());
  };

  const initialChartOptions = {
    chart: {
      id: 'line-chart',
      background: 'white'
    },
    stroke: {
      curve: 'smooth'
    },
    markers: {
      size: 4
    },
    tooltip: {
      y: {
        formatter: function(val) {
          return val;
        }
      }
    },
    xaxis: {
      type: 'datetime',
    }
  };

  const [chartData, setChartData] = useState([]);
  const [filteredDates, setFilteredDates] = useState([]);

  const filterDataByDateRange = (data, startDate, endDate) => {
    if (!startDate || !endDate) return data;
    return data.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const updateChartData = (data) => {
    const leadsDataFiltered = filterDataByDateRange(data, startDate, endDate);

    leadsDataFiltered.sort((a, b) => new Date(a.date) - new Date(b.date));

    const toHumanReadable = (key) => {
      return key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, char => char.toUpperCase());
    };

    const chartDataSeries = Object.keys(leadsDataFiltered[0] || {}).filter(key => key !== 'date').map(key => ({
      name: toHumanReadable(key),
      data: leadsDataFiltered.map(day => day[key])
    }));

    setChartData(chartDataSeries);
    setFilteredDates(leadsDataFiltered.map(day => formatDate(day.date)));
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const chartOptions = {
    ...initialChartOptions,
    xaxis: {
      categories: filteredDates,
      tickAmount: filteredDates.length > 10 ? 10 : filteredDates.length,
    },
    title: {
      text: `Leads Line Chart`,
      align: 'left'
    }
  };

  return (
    <Box pt="20px" overflowX="hidden" w="90%" h="100%">
      <Box
        minH="80vh"
        minW="75vw"
        my="20px"
        rounded="20px"
        p="20px"
        border="1px solid #ccc"
        borderRadius="20px"
        backgroundColor="white"
        position="relative"
        mx="20px"
      >
        <Box display="flex" gap="10px" mt="10px" mb="10px" flexWrap="wrap">
          <FilterCalendar
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onChange={(e) => {
              const { name, value } = e.target;
              if (name === "dateRange") {
                setStartDate(value.startDate);
                setEndDate(value.endDate);
              }
            }}
          />
        {['7', '30', '60', '90'].map((days) => (
  <Button
    key={days}
    borderRadius="5px"
    background={
      activeButton === days
        ? "linear-gradient(180deg, #0177B4 0%, #22ACEE 100%)"
        : "linear-gradient(180deg, #22ACEE 0%, #0177B4 100%)"
    }
    color="#ffffff"
    fontSize="sm"
    colorScheme="teal"
    onClick={() => handleCustomDateChange(parseInt(days, 10))}
    boxShadow={activeButton === days ? "0 0 5px 2px #80ddfa" : "none"}
    isActive={activeButton === days}
  >
    Last {days} Days
  </Button>
))}

        </Box>
        {loading ? (
          <OverlayLoaderComponent isOpen={loading} />
        ) : (
          <LineChart key={JSON.stringify(chartData)} chartData={chartData} chartOptions={chartOptions} />
        )}
      </Box>
    </Box>
  );
};

export default DataCharts;
